import { injectable } from 'inversify';

import { isConstructor } from '@/utils/isConstructor';

// eslint-disable-next-line @typescript-eslint/naming-convention
export function ProxyClass<T extends object>(target: T, className?: string) {
  type TProxy = T & { $proxy: TProxy, $target: T };

  const isCtor = isConstructor(target);
  const ctor = <FunctionConstructor> (isCtor ? target : target.constructor);
  const name = className ?? ctor.name;

  // Explicitly define target name (for debuggability).
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  target[Symbol.toStringTag] = name;

  @injectable()
  abstract class AbstractProxyClass {
    [Symbol.toStringTag]() {
      return name;
    }

    $proxy: TProxy;

    $target: T;

    constructor() {
      const $proxy = <TProxy> new Proxy(target, {
        get: (t, prop, receiver) => {
          if (prop === '$proxy') {
            return $proxy;
          }
          if (prop === '$target') {
            return target;
          }
          // Use existing property/method on the current instance.
          if (Reflect.has(this, prop)) {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-return
            return Reflect.get(this, prop, receiver);
          }

          // Otherwise, proxy to the target.
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const value = Reflect.get(t, prop, receiver) as (() => any) | unknown;

          // eslint-disable-next-line @typescript-eslint/no-unsafe-return
          return typeof value === 'function' ? value.bind(t) : value;
        },
      });

      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      $proxy[Symbol.toStringTag] = name;

      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      // eslint-disable-next-line no-constructor-return
      return $proxy;
    }
  }

  return ({
    [name]: AbstractProxyClass,
  })[name] as unknown as (new() => TProxy);
}
